/**
 * Message-content normalization for optimistic/letterbox dedupe (CurrentChatContext).
 *
 * WHY THIS EXISTS (2026-08-31, "file message shown twice" bug): since CEL-1889 the optimistic
 * bubble pins until server HISTORY contains the sent message — matched by exact content string
 * equality. But the backend REWRITES user content between send and history
 * (utils_message.create_pending_messages_from_user_input):
 *
 *   1. process_human_files(): every file tag's inner path is rewritten from the staging
 *      namespace `human_files/{uuid}/{name}` to the chat namespace `{chat_id}/human_files/{name}`
 *      (plus a uniqueness suffix on filename collision, via get_unique_blob_name).
 *   2. _sanitize_reserved_keywords(): line-leading post-office keywords are escaped to the
 *      {{MESSAGE_*}} convention.
 *
 * So for any send with an attachment, exact equality NEVER matches, the bubble never clears,
 * and the message renders twice (pinned bubble + server-confirmed copy) until a refresh or
 * chat switch wipes optimistic state.
 *
 * Fix: compare a NORMALIZED form on both sides. File tags collapse to a bare marker — the
 * surrounding text plus the tag COUNT/positions must match, which is tolerant of any path
 * rewrite while still distinguishing genuinely different messages. Keyword escaping is
 * mirrored, and whitespace is trimmed.
 *
 * Worst case of the looser predicate: two different sends with identical text AND identical
 * file-tag count clear each other's bubble marginally early — benign (server history is
 * already the source of truth at that point).
 */

// The file-embed tag name, deliberately split so this source file never contains a literal
// tag pair — platform agent tooling treats a full literal tag as an embed directive (same
// reason agent_prompts use the {{MESSAGE_*}} escapes; see AGENTS.md).
// eslint-disable-next-line no-useless-concat -- deliberate: a literal tag pair here would be parsed as an embed directive by agent tooling (see header)
const FILE_TAG = 'SHOW_' + 'FILE';
const FILE_TAG_RE = new RegExp('<' + FILE_TAG + '[^>]*>[\\s\\S]*?</' + FILE_TAG + '>', 'g');

// Mirrors the server's _sanitize_reserved_keywords (py/cellcog/utils_message.py): only
// LINE-LEADING keywords are escaped in stored history.
const RESERVED_KEYWORD_RE = /^MESSAGE (FOR|FROM|END)/gm;

export function normalizeForDedupe(content) {
  if (!content) return '';
  return content
    .replace(FILE_TAG_RE, '[[FILE]]')
    .replace(RESERVED_KEYWORD_RE, '{{MESSAGE_$1}}')
    .trim();
}
